import React, { useEffect } from 'react';
import { DynamicQuestionForm, GroupedQuestionnaireForm } from '../../index';
import Loader from '../../../Loader';
import { ErrorMessage } from '../../ErrorMessage';
import {
  isGroupedQuestionnaire,
  isStandardQuestionnaire,
} from '../../../../utils/questionnaireUtils';
import { useReadonlyQuestionnaire } from '../../../../hooks';

interface FullQuestionnaireReadonlyProps {
  hotelId?: string;
}

export const FullQuestionnaireReadonly: React.FC<
  FullQuestionnaireReadonlyProps
> = ({ hotelId }) => {
  const {
    readonlyQuestionnaireData,
    readonlyQuestionnaireLoading,
    readonlyQuestionnaireError,
    fetchReadonlyQuestionnaire,
  } = useReadonlyQuestionnaire();

  // Fetch readonly questionnaire data when component mounts
  useEffect(() => {
    if (hotelId) {
      fetchReadonlyQuestionnaire();
    }
  }, [hotelId, fetchReadonlyQuestionnaire]);

  // Show loading state
  if (readonlyQuestionnaireLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader message="Loading detailed assessment..." />
      </div>
    );
  }

  // Show error state
  if (readonlyQuestionnaireError) {
    return (
      <div className="p-4">
        <ErrorMessage error={readonlyQuestionnaireError} />
      </div>
    );
  }

  // Show empty state if no data
  if (!readonlyQuestionnaireData) {
    return (
      <div className="flex items-center justify-center h-64 text-gray-500">
        No detailed assessment data available.
      </div>
    );
  }

  // Readonly answers - using the answers from the readonly questionnaire data
  const readonlyAnswers = readonlyQuestionnaireData.answers || {};

  // Empty functions for readonly mode (no updates allowed)
  const noOpUpdate = () => {};

  // Render based on questionnaire type
  if (isGroupedQuestionnaire(readonlyQuestionnaireData)) {
    return (
      <GroupedQuestionnaireForm
        isReadonly
        data={readonlyQuestionnaireData}
        answers={readonlyAnswers}
        onAnswerChange={noOpUpdate}
      />
    );
  }

  if (isStandardQuestionnaire(readonlyQuestionnaireData)) {
    return (
      <div className="space-y-6">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <h3 className="text-lg font-semibold text-blue-800 mb-2">
            Full Detailed Assessment
          </h3>
          <p className="text-blue-600 text-sm">
            This is a read-only view of your complete questionnaire responses.
          </p>
        </div>

        <DynamicQuestionForm
          metadata={readonlyQuestionnaireData.metadata}
          answers={readonlyAnswers}
          onAnswerChange={noOpUpdate}
        />
      </div>
    );
  }

  // Fallback if questionnaire type is not recognized
  return (
    <div className="flex items-center justify-center h-64 text-gray-500">
      Unsupported questionnaire format.
    </div>
  );
};
